(() => {
  if (customElements.get('arista-pdp-dock')) return;

  class AristaPdpDock extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.source = document.getElementById(this.dataset.sourceButton);
      this.button = this.querySelector('[data-arista-pdp-dock-submit]');
      if (!this.source || !this.button) return;

      this.initialized = true;
      this.productInfo = this.closest('product-info');
      this.price = this.querySelector('[data-arista-pdp-dock-price]');
      this.image = this.querySelector('[data-arista-pdp-dock-image]');
      this.sourceMostlyVisible = true;
      this.inputFocused = false;
      this.viewportReduced = false;
      this.viewport = window.visualViewport;
      this.initialViewportHeight = this.viewport?.height || window.innerHeight;
      this.inputSelector = 'input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"]), textarea, select, [contenteditable="true"]';

      try {
        this.variants = JSON.parse(this.querySelector('[data-arista-pdp-variants]')?.textContent || '{}');
      } catch (error) {
        this.variants = {};
      }

      document.body.classList.add('arista-pdp-active');
      this.onClick = () => this.submitFromDock();
      this.onSourceChange = () => this.syncButton();
      this.onFocusIn = (event) => this.handleFocus(event, true);
      this.onFocusOut = () => window.setTimeout(() => this.handleFocus(document.activeElement, false), 0);
      this.onViewportResize = () => this.handleViewportResize();
      this.button.addEventListener('click', this.onClick);
      this.productInfo?.addEventListener('focusin', this.onFocusIn);
      this.productInfo?.addEventListener('focusout', this.onFocusOut);
      this.viewport?.addEventListener('resize', this.onViewportResize);

      this.mutationObserver = new MutationObserver(this.onSourceChange);
      this.mutationObserver.observe(this.source, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.sourceMostlyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.8;
            this.updateVisibility();
          },
          { threshold: [0, 0.8, 1] }
        );
        this.visibilityObserver.observe(this.source);
      } else {
        this.sourceMostlyVisible = false;
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
          this.syncVariant(event.data.variant?.id);
        });
      }

      this.syncVariant(this.dataset.selectedVariant);
      this.syncButton();
      this.updateVisibility();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.button.removeEventListener('click', this.onClick);
      this.productInfo?.removeEventListener('focusin', this.onFocusIn);
      this.productInfo?.removeEventListener('focusout', this.onFocusOut);
      this.viewport?.removeEventListener('resize', this.onViewportResize);
      this.mutationObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.unsubscribeVariant?.();
      document.body.classList.remove('arista-pdp-active');
      document.body.classList.remove('arista-pdp-dock-visible');
      this.initialized = false;
    }

    syncButton() {
      const label = this.source.querySelector('span')?.textContent.trim() || 'Add to cart';
      const disabled = this.source.disabled || this.source.getAttribute('aria-disabled') === 'true';
      this.button.textContent = label;
      this.button.disabled = disabled;
      this.button.setAttribute('aria-disabled', String(disabled));
      this.button.classList.toggle('is-loading', this.source.classList.contains('loading'));
    }

    syncVariant(variantId) {
      const variant = this.variants?.[String(variantId)];
      if (!variant) return;

      if (this.price && variant.price) this.price.textContent = variant.price;
      if (this.image && variant.image) {
        this.image.removeAttribute('srcset');
        this.image.src = variant.image;
        this.image.alt = variant.imageAlt || '';
      }
    }

    handleFocus(target, focusing) {
      const field = target?.closest?.(this.inputSelector);
      this.inputFocused = Boolean(focusing ? field : this.productInfo?.contains(document.activeElement) && document.activeElement?.matches?.(this.inputSelector));
      this.updateVisibility();
    }

    handleViewportResize() {
      const height = this.viewport?.height || window.innerHeight;
      this.viewportReduced = height < this.initialViewportHeight * 0.72;
      this.updateVisibility();
    }

    updateVisibility() {
      const hide = this.sourceMostlyVisible || this.inputFocused || this.viewportReduced;
      this.hidden = hide;
      document.body.classList.toggle('arista-pdp-dock-visible', !hide);
    }

    submitFromDock() {
      if (this.button.disabled) return;
      this.source.click();
      this.dispatchEvent(
        new CustomEvent('arista:pdp-dock-submit', {
          bubbles: true,
          detail: { source: 'sticky-product-action' },
        })
      );
    }
  }

  customElements.define('arista-pdp-dock', AristaPdpDock);
})();
